import { useEffect, useId, useRef, useState } from 'react';

interface Props {
  title: string;
  body: string;
  confirmLabel: string;
  busy?: boolean;
  error?: string | null;
  onConfirm: (password: string) => void;
  onCancel: () => void;
}

/** Повторный ввод пароля перед необратимым действием.
 *
 *  Вход родителя происходит на детском устройстве и оставляет там ключ
 *  надолго: `supabase-js` хранит сессию и обновляет её бессрочно. Один вход
 *  превращает планшет в доступ к журналам всех детей, к почте родителя и к
 *  удалению учётной записи. Ширма с примером от этого не спасает — она
 *  спасает от случайного нажатия.
 *
 *  Отдельным окном, а не строкой на экране: пароль не должен висеть в
 *  разметке дольше, чем длится решение. Помечено `sentry-mask`, как и форма
 *  входа. */
export function PasswordPrompt({
  title, body, confirmLabel, busy, error, onConfirm, onCancel,
}: Props) {
  const [password, setPassword] = useState('');
  const errorId = useId();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const cancel = useRef(onCancel);
  cancel.current = onCancel;

  useEffect(() => {
    const returnTo = document.activeElement as HTMLElement | null;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') cancel.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      returnTo?.focus();
    };
  }, []);

  return (
    <div className="fixed inset-0 z-10 flex items-center justify-center bg-black/40 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="sentry-mask flex w-full max-w-[24rem] flex-col gap-3 rounded-2xl bg-card p-5"
      >
        <h2 className="m-0 text-lg font-semibold">{title}</h2>
        <p className="m-0 text-[0.95rem] text-muted">{body}</p>
        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (password && !busy) onConfirm(password);
          }}
        >
          <label className="flex flex-col gap-1 text-[0.95rem] text-muted">
            Пароль родителя
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              autoFocus
              /* Отказ здесь — про САМО поле: «неверный пароль». Поэтому,
                 кроме немедленного объявления, он связан с полем: диктор,
                 вернувшись к полю, читает причину вместе с ним. Без отказа
                 ни пометки, ни описания — иначе верный пароль звучал бы
                 как неверный. */
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? errorId : undefined}
              className="min-h-12 w-full rounded-xl border-2 border-line bg-card p-3 text-[1.05rem] text-ink"
              onChange={(event) => setPassword(event.target.value)}
            />
          </label>
          {error && (
            <p id={errorId} role="alert" className="m-0 text-[0.95rem] text-incorrect">
              {error}
            </p>
          )}
          <button
            ref={cancelRef}
            type="button"
            onClick={onCancel}
            className="min-h-12 rounded-xl bg-accent p-3 font-semibold text-white"
          >
            Отмена
          </button>
          <button
            type="submit"
            disabled={password === '' || busy}
            className="min-h-12 rounded-xl border-2 border-line bg-card p-3 font-semibold text-incorrect disabled:opacity-50"
          >
            {confirmLabel}
          </button>
        </form>
      </div>
    </div>
  );
}
